import Head from 'next/head'

import type { FC, ReactNode } from 'react'
import type { SeoProps } from '@/types'

import { siteConfig } from '@/constants/site'

const absolute = (origin: string, value: string): string => {
    if (/^https?:\/\//i.test(value)) return value

    return `${origin}${value.startsWith('/') ? '' : '/'}${value}`
}

export const Seo: FC<SeoProps> = ({
    title,
    description,
    ogImage,
    ogImageAlt,
    path = '/',
    origin = '',
    type = 'website',
    noindex = false,
    publishedTime,
    verification
}): ReactNode => {
    const pageTitle = title ? `${title} - ${siteConfig.name}` : siteConfig.name
    const pageDescription = description || siteConfig.description
    const url = origin ? `${origin}${path}` : null
    const image = ogImage && origin ? absolute(origin, ogImage) : null

    return (
        <Head>
            <title>
                {pageTitle}
            </title>

            <meta key='description' name='description' content={pageDescription} />
            {url && <link key='canonical' rel='canonical' href={url} />}

            <meta key='og:site_name' property='og:site_name' content={siteConfig.name} />
            <meta key='og:title' property='og:title' content={pageTitle} />
            <meta key='og:description' property='og:description' content={pageDescription} />
            <meta key='og:type' property='og:type' content={type} />
            {url && <meta key='og:url' property='og:url' content={url} />}
            {image && <meta key='og:image' property='og:image' content={image} />}
            {image && ogImageAlt && <meta key='og:image:alt' property='og:image:alt' content={ogImageAlt} />}
            <meta key='og:locale' property='og:locale' content='en_GB' />

            <meta key='twitter:card' name='twitter:card' content={image ? 'summary_large_image' : 'summary'} />
            <meta key='twitter:title' name='twitter:title' content={pageTitle} />
            <meta key='twitter:description' name='twitter:description' content={pageDescription} />
            {image && <meta key='twitter:image' name='twitter:image' content={image} />}

            {publishedTime && <meta key='article:published_time' property='article:published_time' content={publishedTime} />}
            {noindex && <meta key='robots' name='robots' content='noindex, nofollow' />}
            {verification && <meta key='google-site-verification' name='google-site-verification' content={verification} />}
        </Head>
    )
}
